import {
  Component,
  effect,
  EventEmitter,
  inject,
  input,
  Output,
  signal,
  untracked,
} from "@angular/core";
import { createState } from "../../../../../harmony/angular";
import { PublicationTemplateModule } from "../../../../brand/brand-campaign/publication-template/publication-template.module";
import { FilterOutput } from "../../../../shared/components/filtering/filter.interfaces";
import { SortingOption } from "../../../../shared/components/filtering/sorting-selector/sorting-selector.component";
import { PaginatorComponent } from "../../../../shared/components/paginator/paginator.component";
import { ReloadComponent } from "../../../../shared/components/reload/reload.component";
import { SharedComponentsModule } from "../../../../shared/components/shared-components.module";
import { PublicationTemplateAggregatedMetricsComponent } from "../../../../shared/content/components/publication-template-aggregated-metrics/publication-template-aggregated-metrics.component";
import { PublicationTilePlaceholderComponent } from "../../../../shared/content/components/publication-tile-placeholder/publication-tile-placeholder.component";
import { PublicationTileComponent } from "../../../../shared/content/components/publication-tile/publication-tile.component";
import { GetPublicationAggregatedMetricsInteractor } from "../../../../shared/content/domain/interactors/get-publication-aggregated-metrics.interactor";
import { GetPublicationAggregatedTilesWithSummaryInteractor } from "../../../../shared/content/domain/interactors/get-publication-aggregated-tiles-with-summary.interactor";
import { assert } from "../../../../shared/utils/assert";
import { PartnerCalendarAcceptanceBannerComponent } from "../../components/partner-calendar-acceptance-banner/partner-calendar-acceptance-banner.component";
import { PartnerPublicationTilesState } from "./partner-publication-tiles.state";

@Component({
  standalone: true,
  selector: "app-partner-publication-tiles",
  templateUrl: "./partner-publication-tiles.component.html",
  styleUrls: ["./partner-publication-tiles.component.scss"],
  imports: [
    SharedComponentsModule,
    PaginatorComponent,
    ReloadComponent,
    PublicationTemplateModule,
    PublicationTemplateAggregatedMetricsComponent,
    PartnerCalendarAcceptanceBannerComponent,
    PublicationTilePlaceholderComponent,
    PublicationTileComponent,
  ],
})
export class PartnerPublicationTilesComponent {
  protected readonly state = createState<PartnerPublicationTilesState>({
    $type: "list",
    metrics: undefined,
    publicationTemplates: undefined,
  });
  private readonly getPublicationsWithSummary = inject(
    GetPublicationAggregatedTilesWithSummaryInteractor,
  );
  private readonly getAggregatedMetrics = inject(
    GetPublicationAggregatedMetricsInteractor,
  );
  private readonly pageSize = 10;
  protected readonly EmptyArray = Array.from({ length: this.pageSize });

  public readonly filters = input<FilterOutput[]>([]);
  public readonly sorting = input.required<SortingOption>();
  public readonly isInitialized = input.required<boolean>();
  protected readonly currentPage = signal(1);
  protected readonly totalPages = signal(0);
  @Output() public readonly resetFilters = new EventEmitter<void>();

  constructor() {
    effect(async () => {
      if (!this.isInitialized()) {
        this.updatePublications();
      }
    });
  }

  protected updatePublications(): void {
    const filters = this.filters();
    const sorting = this.sorting();
    const page = this.currentPage();
    untracked(() => {
      this.getPublications(filters, sorting, page);
    });
  }

  protected async getPublications(
    filters: FilterOutput[],
    sorting?: SortingOption,
    pageIndex?: number,
  ): Promise<void> {
    this.state.set({
      $type: "list",
      metrics: undefined,
      publicationTemplates: undefined,
    });

    try {
      this.getAggregatedMetrics
        .execute(this.pageSize, pageIndex, filters, sorting)
        .then((metrics) => {
          if (this.state.$type === "no-results") {
            return;
          }
          assert(this.state.$type === "list");
          this.state.set({
            $type: this.state.$type,
            metrics: metrics,
            publicationTemplates: this.state.publicationTemplates,
          });
        });

      this.getPublicationsWithSummary
        .execute(this.pageSize, pageIndex, filters, sorting)
        .then((list) => {
          if (list.totalSize === 0) {
            this.state.set({ $type: "no-results" });
            return;
          }

          assert(this.state.$type === "list");
          this.state.set({
            $type: "list",
            metrics: this.state.metrics,
            publicationTemplates: list.entries,
          });
          this.totalPages.set(Math.ceil(list.totalSize / this.pageSize));
        });
    } catch {
      this.state.set({ $type: "error" });
    }
  }
}
